import React, { useState, useEffect } from "react";
import { Star, Quote, ThumbsUp, ChevronLeft, ChevronRight } from "lucide-react";
import { ref, onValue } from "firebase/database";
import { rtdb } from "../config/firebase";
import { motion, AnimatePresence } from "framer-motion";

function TestimonialSlider() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [testimonialsData, setTestimonialsData] = useState([]);

  // Load testimonials dari Firebase
  useEffect(() => {
    const testimonialsRef = ref(rtdb, "testimonials");
    onValue(testimonialsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const formatted = Object.values(data).map((item) => ({
        quote: item.keterangan || "",
        client: item.nama || "",
        company: item.perusahaan || "",
        rating: item.rating || 5,
        avatar: item.nama
          ? item.nama
              .split(" ")
              .map((n) => n[0].toUpperCase())
              .join("")
              .slice(0, 2)
          : "NA",
        color: "from-green-500 to-teal-500",
        position: "",
      }));
      setTestimonialsData(formatted);
    });
  }, []);

  // Auto-slide setiap 5 detik
  useEffect(() => {
    if (!testimonialsData.length) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonialsData]);

  const nextTestimonial = () =>
    setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
  const prevTestimonial = () =>
    setActiveIndex(
      (prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length,
    );

  if (!testimonialsData.length) return null;

  return (
    <section className="relative bg-gradient-to-br from-white via-white/95 to-white py-32 px-6 md:px-16 overflow-hidden">

      {/* 🌿 Animated Background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/3 left-1/3 w-96 h-96 bg-green-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-teal-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* ✴️ Header */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="relative z-10 text-center mb-20 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100/10 border border-teal-200/30 rounded-full backdrop-blur-sm mb-6"
        >
          <ThumbsUp className="w-4 h-4 text-teal-500" />
          <span className="text-teal-500 text-sm font-mono uppercase tracking-wider">
            Testimonials
          </span>
        </motion.div>

        <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight">
          <span className="text-gray-900">Apa Kata </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-teal-400 to-green-600">
            Klien Kami?
          </span>
        </h2>

        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"
        ></motion.div>

        <p className="text-lg text-gray-600 leading-relaxed font-light">
          Bukti nyata komitmen kami pada keamanan dan kepuasan Anda
        </p>
      </motion.div>

      {/* 🎠 Testimonial Slider */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white/90 backdrop-blur-sm p-12 shadow-md hover:shadow-xl transition-all duration-700">
          {/* 🔘 Navigation Buttons */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-gray-300 flex items-center justify-center hover:bg-white/40 transition-all duration-300 z-10"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-gray-300 flex items-center justify-center hover:bg-white/40 transition-all duration-300 z-10"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </motion.button>

          {/* 💬 Testimonial Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="text-center max-w-4xl mx-auto space-y-6"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Quote className="w-16 h-16 text-teal-500/50 mx-auto" />
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-2xl md:text-3xl text-gray-900 font-light leading-relaxed italic"
              >
                "{testimonialsData[activeIndex].quote}"
              </motion.p>

              {/* ⭐ Rating */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="flex justify-center gap-1 mb-4"
              >
                {[...Array(testimonialsData[activeIndex].rating)].map(
                  (_, i) => (
                    <Star
                      key={i}
                      className="w-6 h-6 text-yellow-400 fill-yellow-400"
                    />
                  ),
                )}
              </motion.div>

              {/* 👤 Client Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="flex items-center justify-center gap-4"
              >
                <div
                  className={`w-16 h-16 rounded-full bg-gradient-to-br ${testimonialsData[activeIndex].color} flex items-center justify-center shadow-lg`}
                >
                  <span className="text-white font-bold text-xl">
                    {testimonialsData[activeIndex].avatar}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-gray-900 font-bold text-xl">
                    {testimonialsData[activeIndex].client}
                  </p>
                  <p className="text-teal-500 text-sm">
                    {testimonialsData[activeIndex].position}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {testimonialsData[activeIndex].company}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          {/* 🔘 Pagination Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonialsData.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveIndex(index)}
                animate={{
                  scale: index === activeIndex ? [1, 1.2, 1] : 1,
                }}
                transition={{
                  duration: 0.8,
                  repeat: index === activeIndex ? Infinity : 0,
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "w-8 bg-gradient-to-r from-green-500 to-teal-500"
                    : "w-2 bg-gray-300/30 hover:bg-gray-400/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TestimonialSlider;
